<template>
  <Modal position="end" ref="modalRef">
    <template #header>Reschedule showing</template>
    <template #body>
      <RescheduleShowingForm
        v-model:form="form"
        :current-start-time="currentStartTime"
        :tour-type="tourType"
        :property-id="propertyId"
      />
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isLoading" @click="reschedule">Reschedule</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { Button, Modal, useAlertsStore, useForm } from '@tallo/design-system';
import { useNavigation } from '@tallo/investor/navigation';

import { RescheduleShowingDto } from '../interfaces/reschedule-showing.interface';
import { TourType } from '../interfaces/tour-type.enum';
import { useRescheduleShowing } from '../mutations/reschedule-showing.mutation';
import { getShowingStartTime } from '../utils/get-showing-start-time.util';
import {
  buildRescheduleReason,
  createRescheduleShowingFormFields,
  rescheduleShowingFormRules,
  RescheduleShowingFormModel,
} from '../utils/reschedule-showing-form.util';
import RescheduleShowingForm from './reschedule-showing-form.vue';

const alertStore = useAlertsStore();
const { openShowingDetails } = useNavigation();
const { mutateAsync, isLoading } = useRescheduleShowing();

const props = defineProps<{
  showingId: string;
  showingAgentId?: string;
  currentStartTime?: Date | string;
  tourType?: TourType;
  propertyId?: string;
}>();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const fields = createRescheduleShowingFormFields(props.showingAgentId);
const form = useForm<RescheduleShowingFormModel>(fields, rescheduleShowingFormRules(fields, props.tourType));

function open() {
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function reschedule() {
  form.submit(async () => {
    const startTime = getShowingStartTime(form.fields.time as string, form.fields.date as Date);
    const rescheduleShowingDto: RescheduleShowingDto = {
      showingAgentId: form.fields.showingAgentId,
      comment: form.fields.comment || null,
      reason: buildRescheduleReason(form.fields),
      startTime,
    };

    try {
      const { newShowingId } = await mutateAsync({ showingId: props.showingId, rescheduleShowingDto });

      close();
      openShowingDetails(newShowingId, true);
    } catch (error) {
      alertStore.catchError(error);
    }
  });
}

defineExpose({
  open,
});
</script>
